import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {taskGetAll, taskExcel} from '../../../redux/slices/workplan.slice'
import {
  projectLookup,
  staffGetAll,
  constructionSiteLookup,
  blockLookup,
  plotLookup,
} from '../../../redux/slices/lookup.slice'

import {TaskState} from '../../../helpers'
import {useMemoizedColumns} from '../../../utils/performanceOptimizations'

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const selectWorkplanData = useMemo(
    () => (state) => ({
      task: state.workplan.task,
      loading: state.workplan.loading,
    }),
    []
  )

  const selectLookupData = useMemo(
    () => (state) => ({
      projectLookupList: state.lookup.projectLookupList,
      staffLookup: state.lookup.staffLookup,
      constructionSiteLookupList: state.lookup.constructionSiteLookupList,
      blockLookupList: state.lookup.blockLookupList,
      plotLookupList: state.lookup.plotLookupList,
    }),
    []
  )

  const {task, loading} = useSelector(selectWorkplanData)
  const {
    projectLookupList,
    staffLookup,
    constructionSiteLookupList,
    blockLookupList,
    plotLookupList,
  } = useSelector(selectLookupData)

  const [params, setParams] = useState()
  const [project, setProject] = useState(null)
  const [constructionSite, setConstructionSite] = useState(null)
  const [constructionSiteVal, setConstructionSiteVal] = useState(null)
  const [block, setBlock] = useState(null)
  const [blockVal, setBlockVal] = useState(null)
  const [plot, setPlot] = useState(null)

  useEffect(() => {
    if (params) dispatch(taskGetAll(params))
  }, [params, dispatch])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(staffGetAll())
  }, [dispatch])

  useEffect(() => {
    if (project) dispatch(constructionSiteLookup({projectId: project}))
  }, [project, dispatch])

  useEffect(() => {
    if (constructionSiteVal) {
      dispatch(blockLookup({constructionSiteId: constructionSiteVal}))
    }
  }, [constructionSiteVal, dispatch])

  useEffect(() => {
    if (blockVal)
      dispatch(
        plotLookup({
          projectId: project,
          constructionId: constructionSiteVal,
          blockId: blockVal,
        })
      )
  }, [blockVal, project, constructionSiteVal, dispatch])

  useEffect(() => {
    if (constructionSiteLookupList) {
      setConstructionSite(constructionSiteLookupList)
      setBlock(null)
      setPlot(null)
    }

    if (blockLookupList) {
      setBlock(blockLookupList)
      setPlot(null)
    }

    if (plotLookupList) {
      setPlot(plotLookupList)
    }
  }, [constructionSiteLookupList, blockLookupList, plotLookupList])

  const handleChangeSorting = useCallback(
    (item) => setParams((prevState) => ({...prevState, sort: item?.sort, order: item?.order})),
    []
  )

  const Columns = useMemoizedColumns(
    [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCellDouble item={props} double='rootTaskName' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.PROJECT'})}
            className='min-w-125px'
          />
        ),
        accessor: 'projectName',
        Cell: ({...props}) => <CustomCellDouble item={props} double={'constructionSiteName'} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.BLOCKPLOT'})}
            className='min-w-125px'
          />
        ),
        accessor: 'blockName',
        Cell: ({...props}) => <CustomCellDouble item={props} double='plotName' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.STARTDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'startDate',
        Cell: ({...props}) => <CustomCell item={props} date='short' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.ENDDATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'endDate',
        Cell: ({...props}) => <CustomCellDouble item={props} date='short' double='remainingDay' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.COMPANYNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'companyName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.COMPLETIONRATE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'completionRate',
        Cell: ({...props}) => <CustomCell item={props} leftsuffix='%' />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'TASK.SEARCH.TASKSTATENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'taskStateName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleChangeSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleChangeSorting]
  )

  const handleExcel = useCallback(() => {
    dispatch(
      taskExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.TASK'}),
      })
    )
  }, [dispatch, params, intl])

  const handleProjectChange = useCallback((value) => setProject(value), [])
  const handleConstructionSiteChange = useCallback((value) => setConstructionSiteVal(value), [])
  const handleBlockChange = useCallback((value) => setBlockVal(value), [])

  const actionButtons = useMemo(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcel,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'TASK.SEARCH.PROJECT',
            data: projectLookupList,
            name: 'projectId',
            onChange: handleProjectChange,
          },
          {
            title: 'TASK.SEARCH.CONSTRUCTIONSITE',
            data: constructionSite,
            name: 'constructionSiteId',
            onChange: handleConstructionSiteChange,
          },
          {
            title: 'TASK.SEARCH.BLOCK',
            data: block,
            name: 'blockId',
            onChange: handleBlockChange,
          },
          {
            title: 'TASK.SEARCH.PLOTID',
            data: plot,
            name: 'plotId',
          },
          {
            title: 'TASK.SEARCH.TASKSTATENAME',
            data: TaskState,
            name: 'taskStateId',
          },
          {
            title: 'TASK.SEARCH.TASKPLAN',
            data: [
              {
                id: 'true',
                name: intl.formatMessage({id: 'TASK.SEARCH.ROOTTASK'}),
              },
              {
                id: 'false',
                name: intl.formatMessage({id: 'TASK.SEARCH.SUBTASK'}),
              },
            ],
            name: 'onlyRootTask',
          },
          {
            title: 'TASK.SEARCH.STAFFNAME',
            data: staffLookup,
            name: 'staffId',
          },
          {
            title: 'TASK.SEARCH.STARTDATE',
            type: 'date',
            name: 'startDate',
          },
          {
            title: 'TASK.SEARCH.ENDDATE',
            type: 'date',
            name: 'endDate',
          },
        ],
      },
    ],
    [
      handleExcel,
      handleProjectChange,
      handleConstructionSiteChange,
      handleBlockChange,
      projectLookupList,
      constructionSite,
      block,
      plot,
      staffLookup,
      intl,
    ]
  )

  const handleSearch = useCallback(
    (item) => setParams((prevState) => ({...prevState, name: item})),
    []
  )

  const handleFilterSubmit = useCallback((values) => {
    if (values?.projectId) setProject(values?.projectId)
    if (values?.constructionSiteId) setConstructionSiteVal(values?.constructionSiteId)
    if (values?.blockId) setBlockVal(values?.blockId)
    setParams({...values})
  }, [])

  const handlePagination = useCallback(
    (pagination) => setParams((prevState) => ({...prevState, ...pagination})),
    []
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.TASK'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={'TASK'}
        />
        <KTCardBody>
          {!task && <ListLoading />}
          {task && (
            <Table
              items={task}
              cols={Columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
